import { Link, useParams, useNavigate } from "react-router-dom";
import { useState, useEffect } from "react";
import projectQueries from "../../../../requests/queries/ProjectsQuesries";
import ConfirmationModal from "../../../common/ConfirmationModal";
import "./projectView.css";
import colorsQueries from "../../../../requests/queries/ColorsQueries";
import CommandDisplayModal from "./CommandDisplayPanel";

export default function ProjectView() {
    const { id } = useParams();
    const navigate = useNavigate();

    const [project, setProject] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [colors, setColors] = useState([]);
    const [newColor, setNewColor] = useState("#000000");
    const [showExportMenu, setShowExportMenu] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [showCommandDisplayModal, setShowCommandDisplayModal] = useState(false);

    useEffect(() => {
        getProject();
        getColors();
    }, [id]);

    const getProject = async () => {
        setIsLoading(true);
        try {
            const data = await projectQueries.exportProject(id);
            setProject(data);
        } catch (error) {
            console.error("Erreur lors du chargement du projet:", error);
        } finally {
            setTimeout(() => {
                setIsLoading(false);
            }, 300);
        }
    };

    const getColors = async () => {
        const data = await colorsQueries.getColors(id);
        setColors(data);
    };

    const handleAddColor = async () => {
        await colorsQueries.createColor(id, newColor);
        getColors();
    };

    const handleDelete = async () => {
        await projectQueries.deleteProject(id);
        navigate('/projects');
    };

    const handleExportLogs = async () => {
        setIsExporting(true);
        try {
            await projectQueries.downloadProjectLogsCsv(id, project?.name);
        } catch (error) {
            console.error('Erreur export logs:', error);
        } finally {
            setIsExporting(false);
            setShowExportMenu(false);
        }
    };

    const handleExportStats = async () => {
        setIsExporting(true);
        try {
            await projectQueries.downloadProjectStatsCsv(id, project?.name);
        } catch (error) {
            console.error('Erreur export stats:', error);
        } finally {
            setIsExporting(false);
            setShowExportMenu(false);
        }
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('fr-FR', {
            day: 'numeric',
            month: 'long',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    if (isLoading) {
        return (
            <div className="project-view-page">
                <div className="project-view-container">
                    <div className="project-view-loading">
                        <div className="loading-spinner"></div>
                        <span className="loading-text">Chargement du projet...</span>
                    </div>
                </div>
            </div>
        );
    }

    if (!project) {
        return (
            <div className="project-view-page">
                <div className="project-view-container">
                    <Link to="/projects" className="project-view-back">
                        ← Retour aux projets
                    </Link>
                    <div className="project-view-loading">
                        <span className="loading-text">Projet introuvable</span>
                    </div>
                </div>
            </div>
        );
    }

    const screenSize = project.screen_size;
    const pages = project.pages || [];

    return (
        <div className="project-view-page">
            <div className="project-view-container">
                {/* Lien retour */}
                <Link to="/projects" className="project-view-back">
                    ← Retour aux projets
                </Link>

                {/* Header du projet */}
                <header className="project-view-header">
                    <div className="project-view-header-accent"></div>
                    <div className="project-view-header-content">
                        <div className="project-view-header-top">
                            <div className="project-view-title-group">
                                <div className="project-view-icon">🖥️</div>
                                <div className="project-view-title-text">
                                    <h1 className="project-view-title">{project.name}</h1>
                                    <p className="project-view-description">
                                        {project.description || "Aucune description pour ce projet"}
                                    </p>
                                </div>
                            </div>

                            <div className="project-view-actions">
                                {/* Menu Export */}
                                <div className="export-dropdown">
                                    <button 
                                        className="pv-action-btn pv-action-btn-export"
                                        onClick={() => setShowExportMenu(!showExportMenu)}
                                        disabled={isExporting}
                                    >
                                        {isExporting ? '⏳' : '📊'} Export CSV
                                        <span className="export-chevron">{showExportMenu ? '▲' : '▼'}</span>
                                    </button>
                                    {showExportMenu && (
                                        <div className="export-dropdown-menu">
                                            <button onClick={handleExportLogs} disabled={isExporting}>
                                                <span>📋</span> Logs bruts
                                            </button>
                                            <button onClick={handleExportStats} disabled={isExporting}>
                                                <span>📈</span> Statistiques
                                            </button>
                                        </div>
                                    )}
                                </div>
                                <Link 
                                    to={`/project/${id}/editor`} 
                                    className="pv-action-btn pv-action-btn-primary"
                                >
                                    ✏️ Éditer
                                </Link>
                                <Link 
                                    to={`/project/${id}/products`} 
                                    className="pv-action-btn pv-action-btn-green-light"
                                >
                                    🍏 Produits
                                </Link>
                                <Link 
                                    to={`/project/${id}/commands`} 
                                    className="pv-action-btn pv-action-btn-purple-light"
                                >
                                    💰 Commandes
                                </Link>
                                <button
                                    type="button"
                                    className="pv-action-btn pv-action-btn-purple-light"
                                    onClick={() => setShowCommandDisplayModal(true)}
                                >
                                    📺 Écrans
                                </button>
                                <button onClick={() => {
                                        const width = project?.screen_size?.width || 1080;
                                        const height = project?.screen_size?.height || 1920;
                                        const left = (window.screen.width - width) / 2;
                                        const top = (window.screen.height - height) / 2;
                                        window.open(
                                            `https://template-default-pluracore.netlify.app/?project_id=${id}`,
                                            '_blank',
                                            `width=${width},height=${height},left=${left},top=${top},menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=no`
                                        );
                                    }} className="pv-action-btn pv-action-btn-secondary">
                                    👁️ Prévisualiser
                                </button>
                                <button 
                                    className="pv-action-btn pv-action-btn-danger"
                                    onClick={() => setShowDeleteModal(true)}
                                >
                                    🗑️
                                </button>
                            </div>
                        </div>

                        {/* Métadonnées */}
                        <div className="project-view-meta">
                            <div className="project-view-meta-item">
                                <span className="project-view-meta-label">Statut</span>
                                <div className="project-view-status active">
                                    <span>●</span> Actif
                                </div>
                            </div>
                            <div className="project-view-meta-item">
                                <span className="project-view-meta-label">Créé le</span>
                                <span className="project-view-meta-value">
                                    <span className="project-view-meta-icon">📅</span>
                                    {formatDate(project.created_at)}
                                </span>
                            </div>
                            {project.updated_at && (
                                <div className="project-view-meta-item">
                                    <span className="project-view-meta-label">Modifié le</span>
                                    <span className="project-view-meta-value">
                                        <span className="project-view-meta-icon">🔄</span>
                                        {formatDate(project.updated_at)}
                                    </span>
                                </div>
                            )}
                            {screenSize && (
                                <div className="project-view-meta-item">
                                    <span className="project-view-meta-label">Résolution</span>
                                    <span className="project-view-meta-value">
                                        <span className="project-view-meta-icon">📐</span>
                                        {screenSize.width} × {screenSize.height}
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>
                </header>

                <CommandDisplayModal
                    projectId={id}
                    isOpen={showCommandDisplayModal}
                    onClose={() => setShowCommandDisplayModal(false)}
                />

                {/* Contenu principal */}
                <div className="project-view-grid">
                    {/* Section Pages */}
                    <section className="project-view-section">
                        <div className="project-view-section-header">
                            <h2 className="project-view-section-title">
                                <span className="project-view-section-icon">📄</span>
                                Pages
                                <span className="project-view-section-badge">{pages.length}</span>
                            </h2>
                            <Link 
                                to={`/project/${id}/pages/create`} 
                                className="project-view-section-action"
                            >
                                + Ajouter
                            </Link>
                        </div>
                        <div className="project-view-section-content">
                            {pages.length > 0 ? (
                                <div className="pages-list">
                                    {pages.map((page) => (
                                        <Link 
                                            key={page.id} 
                                            to={`/project/${id}/editor?page=${page.id}`}
                                            className="page-item"
                                        >
                                            <div className="page-item-icon">📑</div>
                                            <div className="page-item-info">
                                                <h3 className="page-item-name">{page.name}</h3>
                                                <span className="page-item-meta">
                                                    {page.cells_count || 0} cellule{(page.cells_count || 0) > 1 ? 's' : ''}
                                                </span>
                                            </div>
                                            <span className="page-item-arrow">→</span>
                                        </Link>
                                    ))}
                                </div>
                            ) : (
                                <div className="pages-empty">
                                    <div className="pages-empty-icon">📭</div>
                                    <h3 className="pages-empty-title">Aucune page</h3>
                                    <p className="pages-empty-text">
                                        Créez votre première page pour commencer à construire votre borne
                                    </p>
                                    <Link 
                                        to={`/project/${id}/pages/create`} 
                                        className="project-view-section-action"
                                    >
                                        + Créer une page
                                    </Link>
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Sidebar */}
                    <aside className="project-view-sidebar">
                        {/* Aperçu écran */}
                        {screenSize && (
                            <div className="info-card">
                                <h3 className="info-card-title">
                                    <span className="info-card-icon">🖥️</span>
                                    Taille d'écran
                                </h3>
                                <div className="screen-preview">
                                    <div 
                                        className="screen-preview-box"
                                        style={{
                                            '--screen-ratio': `${screenSize.width}/${screenSize.height}`
                                        }}
                                    >
                                        <span className="screen-preview-label">
                                            {screenSize.ratio_x}:{screenSize.ratio_y}
                                        </span>
                                    </div>
                                    <span className="screen-preview-dimensions">
                                        {screenSize.width} × {screenSize.height} px
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Informations */}
                        <div className="info-card">
                            <h3 className="info-card-title">
                                <span className="info-card-icon">ℹ️</span>
                                Informations
                            </h3>
                            <div className="info-list">
                                <div className="info-list-item">
                                    <span className="info-list-label">ID du projet</span>
                                    <span className="info-list-value">#{project.id}</span>
                                </div>
                                <div className="info-list-item">
                                    <span className="info-list-label">Nombre de pages</span>
                                    <span className="info-list-value">{pages.length}</span>
                                </div>
                                <div className="info-list-item">
                                    <span className="info-list-label">Orientation</span>
                                    <span className="info-list-value">
                                        {screenSize && screenSize.width > screenSize.height 
                                            ? '🖥️ Paysage' 
                                            : '📱 Portrait'}
                                    </span>
                                </div>
                            </div>
                        </div>

                        {/* Actions rapides */}
                        <div className="info-card">
                            <h3 className="info-card-title">
                                <span className="info-card-icon">⚡</span>
                                Actions rapides
                            </h3>
                            <div className="quick-actions">
                                <Link 
                                    to={`/project/${id}/stats`} 
                                    className="quick-action-btn stats-btn"
                                >
                                    <span className="quick-action-icon">📊</span>
                                    Voir les statistiques
                                </Link>
                                <Link 
                                    to={`/project/${id}/settings`} 
                                    className="quick-action-btn"
                                >
                                    <span className="quick-action-icon">⚙️</span>
                                    Paramètres du projet
                                </Link>
                                <button 
                                    className="quick-action-btn deploy-btn"
                                    onClick={async () => {
                                        try {
                                            await projectQueries.downloadProjectZip(id, project?.name);
                                        } catch (error) {
                                            console.error('Erreur téléchargement ZIP:', error);
                                        }
                                    }}
                                >
                                    <span className="quick-action-icon">📦</span>
                                    Télécharger ZIP déployable
                                </button>
                                <button className="quick-action-btn">
                                    <span className="quick-action-icon">📋</span>
                                    Dupliquer le projet
                                </button>
                            </div>
                        </div>
                    </aside>
                </div>

                {/* Section Couleurs */}
                <div className="project-view-grid project-view-grid-colors">
                    <section className="project-view-section">
                        <div className="project-view-section-header">
                            <h2 className="project-view-section-title">
                                <span className="project-view-section-icon">🎨</span>
                                Couleurs
                            </h2>
                        </div>
                        <div className="project-view-section-content">
                            <div className="colors-list">
                                {colors.map((color) => (
                                    <div key={color.id} className="color-item">
                                        <div className="color-item-color" style={{ backgroundColor: color.color }}></div>
                                        <button className="color-item-btn-delete" onClick={async () => { await colorsQueries.deleteColor(color.id); getColors(); }}>
                                            🗑️
                                        </button>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </section>
                    <section className="project-view-section">
                        <div className="project-view-section-header">
                            <h2 className="project-view-section-title">
                                <span className="project-view-section-icon">🎨</span>
                                Ajouter une couleur
                            </h2>
                        </div>
                        <div className="project-view-section-content">
                            <div className="colors-list">
                                <div className="color-item">
                                    <input type="color" className="color-item-color" value={newColor} onChange={(e) => setNewColor(e.target.value)} />
                                    <button className="color-item-btn project-view-section-action" onClick={() => handleAddColor("#000000")}>
                                        Ajouter
                                    </button>
                                </div>
                            </div>
                        </div>
                    </section>
                </div>

                {/* Modal de suppression */}
                {showDeleteModal && (
                    <ConfirmationModal
                        title="Supprimer le projet ?"
                        icon="🗑️"
                        iconType="danger"
                        confirmText="Supprimer définitivement"
                        cancelText="Annuler"
                        onConfirm={handleDelete}
                        onCancel={() => setShowDeleteModal(false)}
                    >
                        Êtes-vous sûr de vouloir supprimer <strong>"{project.name}"</strong> ?
                        <br />
                        Cette action supprimera également toutes les pages et cellules associées.
                    </ConfirmationModal>
                )}
            </div>
        </div>
    );
}
